import { useMemo } from 'react';

import { PipelinesTimeseriesData } from '../components/list-sidebar/types';

export type PipelinesTimeseriesAverage = {
  lastWeekValue?: number;
  difference: number;
  average: number;
  standardDeviation: number;
};

export function usePipelinesTimeseriesAverage(
  data: PipelinesTimeseriesData[],
  propertyName: string,
  useRelativeDifference = false
): PipelinesTimeseriesAverage {
  const { lastWeekValue, difference, average, standardDeviation } =
    useMemo(() => {
      const lastWeekVal = data[data.length - 1]?.data?.[propertyName];

      const getNonEmptyData = (
        timeseriesData: PipelinesTimeseriesData[] = data
      ) => timeseriesData.filter(d => d.data !== null);

      const calculateAverage = (
        timeseriesData: PipelinesTimeseriesData[] = data
      ): number => {
        if (!timeseriesData.length) return 0;

        const filteredData = getNonEmptyData(timeseriesData);
        const sum = filteredData.reduce(
          (acc, cur) => acc + (cur.data?.[propertyName] || 0),
          0
        );

        return sum / filteredData.length;
      };

      const calculateDifference = (): number => {
        if (lastWeekVal === undefined) return 0;
        if (getNonEmptyData().length < 2) return 0;

        const previousWeeksAverage = calculateAverage(data.slice(0, -1));
        const calculatedDifference = lastWeekVal - previousWeeksAverage;

        if (!useRelativeDifference) return calculatedDifference;
        return calculatedDifference / (previousWeeksAverage || 1);
      };

      const calculateStandardDeviation = (
        timeseriesData: PipelinesTimeseriesData[] = data
      ): number => {
        const dataAverage = calculateAverage(timeseriesData);

        if (!timeseriesData.length) return 0;

        const filteredData = getNonEmptyData(timeseriesData);
        const sumDeviationSquared = filteredData.reduce(
          (acc, cur) =>
            acc + Math.pow((cur.data?.[propertyName] || 0) - dataAverage, 2),
          0
        );

        return Math.sqrt(sumDeviationSquared / filteredData.length);
      };

      return {
        lastWeekValue: lastWeekVal,
        average: calculateAverage(),
        difference: calculateDifference(),
        standardDeviation: calculateStandardDeviation(),
      };
    }, [data, propertyName, useRelativeDifference]);

  return {
    lastWeekValue,
    difference,
    average,
    standardDeviation,
  };
}
